import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  SpeakerWaveIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/store/auth'
import toast from 'react-hot-toast'

// ============================================================================
// PRECISION BRUTALIST Memory Modal
// ============================================================================

interface DeviceInfo {
  phone: string
  laptop: string
  earphones: string
  wearable: string
}

interface MemoryModalProps {
  isOpen: boolean
  onClose: () => void
}

const deviceCategories = [
  {
    id: 'phone',
    label: 'Smartphone',
    icon: DevicePhoneMobileIcon,
    placeholder: 'e.g., iPhone 15 Pro, Galaxy S24',
    examples: ['iPhone 15 Pro', 'Samsung Galaxy S24', 'Google Pixel 8', 'OnePlus 12']
  },
  {
    id: 'laptop',
    label: 'Laptop',
    icon: ComputerDesktopIcon,
    placeholder: 'e.g., MacBook Pro M3, ThinkPad X1',
    examples: ['MacBook Pro M3', 'Dell XPS 13', 'ThinkPad X1 Carbon', 'Surface Laptop']
  },
  {
    id: 'earphones',
    label: 'Audio',
    icon: SpeakerWaveIcon,
    placeholder: 'e.g., AirPods Pro, Sony XM5',
    examples: ['AirPods Pro 2', 'Sony WH-1000XM5', 'Bose QC Ultra', 'Galaxy Buds Pro']
  },
  {
    id: 'wearable',
    label: 'Wearable',
    icon: ClockIcon,
    placeholder: 'e.g., Apple Watch Ultra 2',
    examples: ['Apple Watch Ultra 2', 'Galaxy Watch 6', 'Garmin Fenix 7', 'Oura Ring']
  }
]

const ease = [0.16, 1, 0.3, 1]

export function MemoryModal({ isOpen, onClose }: MemoryModalProps) {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    phone: '',
    laptop: '',
    earphones: '',
    wearable: ''
  })
  const [currentField, setCurrentField] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { user, token, isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isOpen) loadDeviceInfo()
  }, [isOpen])

  const loadDeviceInfo = async () => {
    setIsLoading(true)
    try {
      if (!isAuthenticated) {
        setIsLoading(false)
        return
      }

      const currentToken = token || localStorage.getItem('jpdz_token')
      const headers: Record<string, string> = {}
      if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`

      const response = await fetch('/api/device/get-device-info', {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.device_info) {
          setDeviceInfo({
            phone: data.device_info.phone || '',
            laptop: data.device_info.laptop || '',
            earphones: data.device_info.earphones || '',
            wearable: data.device_info.wearable || ''
          })
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeviceChange = (category: string, value: string) => {
    setDeviceInfo(prev => ({ ...prev, [category]: value }))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      if (!isAuthenticated) {
        toast.error('Please log in to save your devices')
        setIsSubmitting(false)
        return
      }

      const currentToken = token || localStorage.getItem('jpdz_token')
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`

      const response = await fetch('/api/device/device-info', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(deviceInfo)
      })

      if (response.ok) {
        toast.success('Devices saved successfully')
        onClose()
      } else if (response.status === 401) {
        toast.error('Session expired. Please log in again.')
        localStorage.removeItem('jpdz_token')
      } else {
        throw new Error('Failed to save')
      }
    } catch (error) {
      toast.error('Failed to save devices')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getFilledCount = () => Object.values(deviceInfo).filter(v => v.trim() !== '').length

  const selectExample = (category: string, example: string) => {
    handleDeviceChange(category, example)
    setCurrentField(null)
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease }}
          className="relative w-full max-w-3xl max-h-[90vh] bg-[#050505] border border-white/[0.06] overflow-hidden"
        >
          {/* Ambient glow */}
          <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-accent/[0.05] to-transparent pointer-events-none" />
          <div className="absolute top-0 right-0 w-60 h-60 bg-accent/[0.03] blur-[100px] pointer-events-none" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 z-10 p-2 hover:bg-white/5 transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-white/40 hover:text-white" />
          </button>

          {/* Content */}
          <div className="overflow-y-auto max-h-[90vh] p-8 relative">
            {/* Header */}
            <div className="mb-10">
              <div className="flex items-center gap-4 mb-6">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="w-14 h-14 bg-gradient-to-br from-accent to-orange-600 flex items-center justify-center shadow-lg shadow-accent/30"
                >
                  <CpuChipIcon className="w-7 h-7 text-black" />
                </motion.div>
                <div>
                  <h2 className="font-display font-bold text-3xl text-white">
                    My Tech
                  </h2>
                  <p className="font-mono text-[10px] tracking-[0.2em] text-white/30 uppercase mt-1">
                    Personalization Settings
                  </p>
                </div>
              </div>

              <p className="font-body text-white/50 max-w-lg">
                Tell us what devices you own so we can give you personalized recommendations.
              </p>

              {user && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/20"
                >
                  <SparklesIcon className="w-4 h-4 text-accent" />
                  <span className="font-mono text-xs text-accent">
                    {user.username}, AI will use this for better recommendations
                  </span>
                </motion.div>
              )}
            </div>

            {/* Progress */}
            {!isLoading && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-white/30 uppercase">
                    Devices Added
                  </span>
                  <span className="font-mono text-sm text-accent font-bold">
                    {getFilledCount()}/{deviceCategories.length}
                  </span>
                </div>
                <div className="h-1 bg-white/[0.06] overflow-hidden">
                  <motion.div
                    animate={{ width: `${(getFilledCount() / deviceCategories.length) * 100}%` }}
                    className="h-full bg-gradient-to-r from-accent to-orange-500"
                    transition={{ duration: 0.4, ease }}
                  />
                </div>
              </div>
            )}

            {/* Loading */}
            {isLoading ? (
              <div className="py-16 text-center">
                <motion.div
                  className="w-12 h-12 border-2 border-accent/30 border-t-accent mx-auto mb-4"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                <p className="font-mono text-xs text-white/30">Loading your devices...</p>
              </div>
            ) : (
              <>
                {/* Device Grid */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {deviceCategories.map((category, idx) => (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05, ease }}
                      className="group"
                    >
                      <div className={`bg-[#0a0a0a] border transition-all p-5 ${currentField === category.id
                          ? 'border-accent/40'
                          : 'border-white/[0.06] hover:border-white/[0.1]'
                        }`}>
                        {/* Category Header */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-10 h-10 flex items-center justify-center transition-colors ${deviceInfo[category.id as keyof DeviceInfo]
                              ? 'bg-accent text-black'
                              : 'bg-white/[0.03] text-white/40'
                            }`}>
                            <category.icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-display font-semibold text-white">{category.label}</h3>
                            {deviceInfo[category.id as keyof DeviceInfo] && (
                              <motion.span
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="font-mono text-[9px] tracking-wider text-accent uppercase flex items-center gap-1"
                              >
                                <CheckIcon className="w-3 h-3" />
                                Added
                              </motion.span>
                            )}
                          </div>
                        </div>

                        {/* Input */}
                        <div className="relative">
                          <input
                            type="text"
                            value={deviceInfo[category.id as keyof DeviceInfo]}
                            onChange={(e) => handleDeviceChange(category.id, e.target.value)}
                            onFocus={() => setCurrentField(category.id)}
                            onBlur={() => setTimeout(() => setCurrentField(null), 150)}
                            placeholder={category.placeholder}
                            className="w-full bg-black border border-white/[0.08] px-4 py-3 text-white text-sm placeholder-white/20 focus:border-accent/50 focus:outline-none transition-colors font-body"
                          />
                          {deviceInfo[category.id as keyof DeviceInfo] && (
                            <button
                              onClick={() => handleDeviceChange(category.id, '')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Quick Examples */}
                        <AnimatePresence>
                          {currentField === category.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-3 overflow-hidden"
                            >
                              <p className="font-mono text-[9px] tracking-wider text-white/20 uppercase mb-2">
                                Quick select
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {category.examples.map((example) => (
                                  <button
                                    key={example}
                                    onClick={() => selectExample(category.id, example)}
                                    className="px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] hover:border-accent/40 hover:bg-accent/5 text-white/50 hover:text-white font-mono text-[11px] transition-all"
                                  >
                                    {example}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-6 border-t border-white/[0.06]">
                  <p className="font-mono text-[9px] tracking-wider text-white/20 max-w-[200px]">
                    Data is stored securely and only used for recommendations
                  </p>

                  <div className="flex items-center gap-3">
                    <motion.button
                      onClick={onClose}
                      whileHover={{ x: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 text-white/40 hover:text-white font-display font-medium transition-colors"
                    >
                      Cancel
                    </motion.button>

                    <motion.button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-8 py-3 bg-gradient-to-r from-accent to-orange-500 text-black font-display font-bold shadow-lg shadow-accent/20 hover:shadow-accent/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <motion.div
                            className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-5 h-5" />
                          <span>Save</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
